/* AstroSong UGC Upload — inline card flow (no modal)
 * Replaces the legacy ezfiledrop external link.
 * Pairs with the Cloudflare Worker at worker/ugc-upload/.
 *
 * The Liquid renders a multi-state zone inside the existing share-video card
 * when section setting `worker_endpoint` is configured. This script wires
 * file selection, chunked Drive upload, Shopify discount minting, and state
 * persistence into that zone.
 *
 * Reads config from data attributes on #astroSongReveal:
 *   data-worker-endpoint, data-handle, data-order-number, data-sunsign,
 *   data-incentive-variant, data-terms-url, data-ugc-promo, data-ugc-count
 *
 * "Already uploaded" detection cascade:
 *   1. data-ugc-promo (Liquid metafield render — cross-device)
 *   2. localStorage backup (instant, survives Shopify CDN cache lag)
 *   3. GET /status (Worker — definitive fallback fetched async)
 */
(function () {
  var root = document.getElementById('astroSongReveal');
  if (!root) return;

  var WORKER_ENDPOINT = (root.dataset.workerEndpoint || '').replace(/\/$/, '');
  if (!WORKER_ENDPOINT) return;

  var card = document.getElementById('uploadSection');
  var zone = card && card.querySelector('[data-ugc-zone]');
  if (!zone) return;

  /* ---------- Config + constants ---------- */
  var CFG = {
    pageHandle: root.dataset.handle || '',
    orderNumber: root.dataset.orderNumber || '',
    sunSign: (root.dataset.sunsign || '').toLowerCase(),
    incentiveVariant: root.dataset.incentiveVariant || 'v1_free_astrosong',
    initialPromoCode: root.dataset.ugcPromo || '',
    initialUploadCount: parseInt(root.dataset.ugcCount || '0', 10) || 0
  };
  var MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;
  var MAX_UPLOADS = 3;
  var CHUNK_SIZE = 4 * 1024 * 1024; /* 4 MB — smaller = more frequent progress updates + cheaper retries */
  var MAX_NOTE_LEN = 500;
  var ALLOWED_EXTS = ['mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv'];
  var ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm', 'video/x-msvideo', 'video/x-matroska'];
  var STORAGE_KEY = 'as_ugc_state_' + CFG.pageHandle;
  var SUPPORT_EMAIL = 'hello@astro-song.com';

  /* ---------- Screen Wake Lock (for the duration of the UPLOAD) ----------
   * The recorder holds a wake lock + the live camera keeps the screen awake DURING
   * recording — but after Send the camera is off and that lock is released, so the
   * ~2-min full-length upload runs with nothing keeping iOS awake → it auto-locks
   * → Safari suspends the page → the in-flight upload stalls. We re-hold a 'screen'
   * lock for the byte transfer (and the finalize POST). Mirrors the recorder's
   * proven pattern. Graceful no-op where the API is absent; in iOS Low Power Mode
   * the request is REJECTED (.catch) and the resume-on-wake backstop takes over. */
  var wakeLock = null;
  var wakeWanted = false;
  var wakePending = false;
  function acquireWakeLock() {
    wakeWanted = true;
    if (wakeLock || wakePending) return; /* idempotent — never orphan an existing/in-flight lock */
    try {
      if (navigator.wakeLock && navigator.wakeLock.request) {
        wakePending = true;
        navigator.wakeLock.request('screen').then(function (wl) {
          wakePending = false;
          if (!wakeWanted) { try { wl.release(); } catch (e) {} return; }
          wakeLock = wl;
          /* The OS auto-releases the lock when the page hides; null our ref so the
             resume-on-wake re-request fires exactly once. */
          try { wl.addEventListener('release', function () { wakeLock = null; }); } catch (e) {}
        }).catch(function () { wakePending = false; });
      }
    } catch (e) { wakePending = false; }
  }
  function releaseWakeLock() {
    wakeWanted = false;
    if (wakeLock) { try { wakeLock.release(); } catch (e) {} wakeLock = null; }
  }

  /* ---------- State ----------
   * `pendingFileId` is the Drive file ID we got back from a successful upload that
   * has NOT yet been successfully finalized. If this is set, the next "Retry"
   * action skips re-upload entirely and just re-calls finalize — preventing the
   * Drive duplicates we used to create on every finalize-side failure. */
  var state = {
    view: 'idle',
    file: null,
    note: '',
    promoCode: CFG.initialPromoCode,
    uploadCount: CFG.initialUploadCount,
    sessionUri: null,
    uploadIndex: 0,
    progress: 0,
    pendingFileId: null,
    pendingUploadIndex: 0
  };

  /* ---------- GA4 ---------- */
  function gtm(event, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      payload.page_handle = CFG.pageHandle;
      payload.order_number = CFG.orderNumber;
      payload.sun_sign = CFG.sunSign;
      payload.incentive_variant = CFG.incentiveVariant;
      if (params) for (var k in params) payload[k] = params[k];
      window.dataLayer.push(payload);
    } catch (e) {}
  }

  /* ---------- localStorage persistence ---------- */
  function loadStored() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function saveStored(patch) {
    try {
      var cur = loadStored();
      for (var k in patch) cur[k] = patch[k];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cur));
    } catch (e) {}
  }
  function clearStoredFile() {
    try {
      var cur = loadStored();
      delete cur.fileMeta;
      delete cur.sessionUri;
      delete cur.uploadIndex;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cur));
    } catch (e) {}
  }
  function clearStoredPending() {
    try {
      var cur = loadStored();
      delete cur.pendingFileId;
      delete cur.pendingUploadIndex;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cur));
    } catch (e) {}
  }

  /* ---------- IndexedDB: persist the recorded video ON-DEVICE ----------
   * So a failed/interrupted upload (or a reload, or she switched apps) never
   * loses her video. Keyed per page handle (one pending recording per page).
   * The recorder saves the blob on stop; the card offers to resume on reload. */
  var IDB_NAME = 'astrosong-ugc';
  var IDB_STORE = 'recordings';
  function idbOpen() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) { reject(new Error('no-idb')); return; }
      var req;
      try { req = indexedDB.open(IDB_NAME, 1); } catch (e) { reject(e); return; }
      req.onupgradeneeded = function () { try { req.result.createObjectStore(IDB_STORE); } catch (e) {} };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
    });
  }
  function idbTx(mode, fn) {
    return idbOpen().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(IDB_STORE, mode);
        var store = tx.objectStore(IDB_STORE);
        var out = fn(store);
        tx.oncomplete = function () { resolve(out && out.result !== undefined ? out.result : undefined); };
        tx.onerror = function () { reject(tx.error); };
        tx.onabort = function () { reject(tx.error || new Error('idb-abort')); };
      });
    });
  }
  window.astrosongUGCSavePending = function (blob, note) {
    return idbTx('readwrite', function (s) { s.put({ blob: blob, note: note || '', ts: Date.now(), type: blob.type }, CFG.pageHandle); })
      .catch(function (e) {
        /* Storage full or unsupported — non-fatal: the in-session send still
           works from memory; only resume-after-reload won't be available. */
        console.warn('[UGC] could not persist recording locally', e);
        gtm('ugc_idb_save_failed', { reason: (e && e.name) || 'unknown' });
      });
  };
  window.astrosongUGCClearPending = function () {
    return idbTx('readwrite', function (s) { s.delete(CFG.pageHandle); }).catch(function () {});
  };
  function idbGetPending() {
    return idbTx('readonly', function (s) { return s.get(CFG.pageHandle); });
  }
  /* Exposed so the RECORDER can surface a recovered reaction CLEARLY (auto-open the
     preview) on load, independent of this card's promo-code-gated success view. */
  window.astrosongUGCGetPending = function () { return idbGetPending(); };

  /* ---------- Validation ---------- */
  function getExt(name) {
    var i = (name || '').lastIndexOf('.');
    if (i < 0) return '';
    return name.slice(i + 1).toLowerCase();
  }
  function validateFile(file) {
    if (!file) return 'No file selected.';
    if (file.size > MAX_FILE_SIZE) return 'Video is too large. Max 5 GB.';
    if (file.size < 1024) return 'File seems too small to be a real video.';
    var typeOk = file.type && ALLOWED_TYPES.indexOf(file.type) !== -1;
    var extOk = ALLOWED_EXTS.indexOf(getExt(file.name)) !== -1;
    if (!typeOk && !extOk) return 'Unsupported file type. Use MP4, MOV, M4V, WEBM, AVI, or MKV.';
    return null;
  }
  function formatBytes(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
    if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
  }

  /* ---------- Worker calls ---------- */
  function workerFetch(path, opts) {
    return fetch(WORKER_ENDPOINT + path, opts).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.error || ('HTTP ' + res.status));
          err.status = res.status;
          err.detail = data;
          throw err;
        }
        return data;
      });
    });
  }
  function callInitUpload(file) {
    return workerFetch('/init-upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page_handle: CFG.pageHandle,
        order_number: CFG.orderNumber,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type || 'video/mp4',
        sun_sign: CFG.sunSign,
        incentive_variant: CFG.incentiveVariant
      })
    });
  }
  function callFinalizeUpload(fileId, note, uploadIndex) {
    return workerFetch('/finalize-upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page_handle: CFG.pageHandle,
        order_number: CFG.orderNumber,
        file_id: fileId,
        note: note || '',
        sun_sign: CFG.sunSign,
        incentive_variant: CFG.incentiveVariant,
        upload_index: uploadIndex,
        song_start_ms: (state.songStartMs != null ? Math.round(state.songStartMs) : '')
      })
    });
  }
  function callStatus() {
    return workerFetch('/status?page_handle=' + encodeURIComponent(CFG.pageHandle), { method: 'GET' });
  }

  /* ---------- Drive resumable chunked upload ---------- */
  function queryUploadOffset(sessionUri, total) {
    return fetch(sessionUri, {
      method: 'PUT',
      headers: { 'Content-Range': 'bytes */' + total }
    }).then(function (res) {
      if (res.status === 200 || res.status === 201) {
        return res.json().then(function (m) { return { complete: true, meta: m }; });
      }
      if (res.status === 308) {
        var rng = res.headers.get('Range');
        if (rng) {
          var m = rng.match(/bytes=\d+-(\d+)/);
          if (m) return { complete: false, offset: parseInt(m[1], 10) + 1 };
        }
        return { complete: false, offset: 0 };
      }
      throw new Error('Status query failed: ' + res.status);
    });
  }
  function uploadFileToSession(file, sessionUri, onProgress) {
    /* Hold the screen awake for the whole ~2-min byte transfer (released on settle
       below). Acquired here — the single function every chunked upload funnels
       through — not around the sub-second init POST that precedes it. */
    acquireWakeLock();
    var p = new Promise(function (resolve, reject) {
      var total = file.size;
      var offset = 0;
      var aborted = false;
      var recovering = false;
      var globalRetries = 0;
      var MAX_GLOBAL_RETRIES = 6;
      var currentXhr = null;

      state.abort = function () { aborted = true; if (currentXhr) try { currentXhr.abort(); } catch(e){} reject(new Error('cancelled')); };
      /* Resume-on-wake: re-acquire the lock (the OS dropped it when hidden), kill
         the suspended/dead chunk XHR, and resume from the last CONFIRMED byte via
         the EXISTING 308-Range probe. Budget-free + zero-backoff (a screen lock is
         not a transport failure). Nulled on settle so it no-ops at every other
         time — which is exactly the "a chunk transfer is mid-flight" gate the
         visibility handler tests with `typeof state.resumeKick === 'function'`. */
      state.resumeKick = function () {
        if (aborted || recovering) return;
        acquireWakeLock();
        if (currentXhr) { try { currentXhr.abort(); } catch (e) {} }
        recoverAndRetry('wake', true);
      };
      var lastStatus = null;
      var lastResponseText = '';

      console.log('[UGC] upload start, size=' + total + ' bytes, chunks of ' + CHUNK_SIZE);

      function next() {
        if (aborted) return;
        if (offset >= total) return;
        var end = Math.min(offset + CHUNK_SIZE, total);
        var chunk = file.slice(offset, end);
        var range = 'bytes ' + offset + '-' + (end - 1) + '/' + total;
        var isLast = (end === total);

        var xhr = new XMLHttpRequest();
        currentXhr = xhr;
        xhr.open('PUT', sessionUri, true);
        xhr.setRequestHeader('Content-Range', range);
        /* Mobile 4G stalls silently for 5-30 min by default. 60s per-chunk timeout
           lets recoverAndRetry kick in fast and probe the session for the real
           offset, rather than the user staring at a frozen progress bar. */
        xhr.timeout = 60000;
        xhr.upload.onprogress = function (e) {
          if (e.lengthComputable && onProgress) onProgress((offset + e.loaded) / total);
        };
        xhr.onload = function () {
          var s = xhr.status;
          lastStatus = s;
          lastResponseText = xhr.responseText || '';
          console.log('[UGC] chunk ' + range + (isLast ? ' [LAST]' : '') + ' → HTTP ' + s);
          if (s === 200 || s === 201) {
            if (onProgress) onProgress(1);
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (e) { reject(new Error('Drive returned malformed JSON: ' + xhr.responseText.slice(0, 200))); }
            return;
          }
          if (s === 308) {
            offset = end;
            if (onProgress) onProgress(offset / total);
            next();
            return;
          }
          console.warn('[UGC] unexpected HTTP ' + s + ', body=' + xhr.responseText.slice(0, 300));
          recoverAndRetry('http_' + s);
        };
        xhr.onerror = function () { console.warn('[UGC] xhr.onerror'); recoverAndRetry('network'); };
        xhr.ontimeout = function () { console.warn('[UGC] xhr.ontimeout'); recoverAndRetry('timeout'); };
        xhr.send(chunk);
      }

      function recoverAndRetry(reason, wake) {
        if (aborted) return;
        /* A probe is already pending — don't stack a parallel next() loop. NOTE:
           this also de-dupes ORDINARY network retries (a stray ontimeout/onerror
           firing while a retry setTimeout is already scheduled), not just wake
           kicks — that is intentional hardening; do NOT "restore" stacked timers. */
        if (recovering) return;
        /* A wake (screen-lock resume) is NOT a transport failure: it must not
           consume the failure budget that genuine network errors rely on, and it
           must resume with zero backoff (the founder may lock/wake several times). */
        if (!wake && globalRetries >= MAX_GLOBAL_RETRIES) {
          var msg = 'Upload failed after ' + MAX_GLOBAL_RETRIES + ' retries. Last status: ' + lastStatus + (lastResponseText ? ' — ' + lastResponseText.slice(0, 200) : '');
          console.error('[UGC] ' + msg);
          reject(new Error(msg));
          return;
        }
        if (!wake) globalRetries++;
        var backoff = wake ? 0 : 800 * Math.pow(2, globalRetries - 1);
        console.log('[UGC] ' + (wake ? 'wake-resume' : 'retry ' + globalRetries + '/' + MAX_GLOBAL_RETRIES) + ' (' + reason + ') in ' + backoff + 'ms');
        recovering = true;
        setTimeout(function () {
          if (aborted) { recovering = false; return; }
          queryUploadOffset(sessionUri, total).then(function (s) {
            recovering = false;
            if (s.complete) { console.log('[UGC] recovery: already complete'); resolve(s.meta); return; }
            console.log('[UGC] recovery: resuming at offset ' + s.offset);
            offset = s.offset;
            if (onProgress) onProgress(offset / total);
            next();
          }).catch(function (e) {
            recovering = false;
            console.warn('[UGC] queryUploadOffset failed:', e.message);
            next();
          });
        }, backoff);
      }

      next();
    });
    /* The inner promise settles exactly once (success 200/201, retry-exhausted
       reject, malformed-JSON reject, or the Cancel button's 'cancelled' reject):
       release the lock + retire the per-upload hooks on EVERY terminal path so no
       sentinel dangles and a late wake event after the upload ended is a no-op. */
    function settle() { releaseWakeLock(); state.resumeKick = null; }
    p.then(settle, settle);
    return p;
  }

  /* ---------- DOM helpers ---------- */
  function $(sel) { return zone.querySelector(sel); }
  function $$(sel) { return zone.querySelectorAll(sel); }
  function setView(view) {
    state.view = view;
    var views = $$('.as-ugc-view');
    for (var i = 0; i < views.length; i++) views[i].hidden = (views[i].dataset.view !== view);
    /* Hide the static card heading (title + desc) in states where the view renders its own */
    var heading = card.querySelector('[data-ugc-heading]');
    if (heading) {
      var hideHeading = (view === 'selected' || view === 'uploading' || view === 'success' || view === 'limit' || view === 'error' || view === 'resume');
      heading.style.display = hideHeading ? 'none' : '';
    }
    /* The idle view doubles as the re-upload entry point. When a reward code
       already exists, show a "back to my code" link so a mis-tap on "Upload
       another version" isn't a dead end — the code is still saved, it was just
       never rendered in idle, which read to the customer as "my code vanished". */
    if (view === 'idle') {
      var backBtn = zone.querySelector('[data-ugc-back-to-code]');
      if (backBtn) backBtn.hidden = !state.promoCode;
    }
  }

  /* ---------- Renderers ---------- */
  function renderSelectedView() {
    if (!state.file) return;
    $('[data-ugc-filename]').textContent = state.file.name;
    $('[data-ugc-filemeta]').textContent = formatBytes(state.file.size);
    var noteInput = $('#ugcNoteInput');
    if (noteInput && state.note && !noteInput.value) noteInput.value = state.note;
    updateNoteCounter();
  }
  function renderUploadingView() {
    var pct = Math.round(state.progress * 100);
    var fill = $('[data-ugc-progress-fill]');
    var pctEl = $('[data-ugc-progress-pct]');
    var bytesEl = $('[data-ugc-progress-bytes]');
    if (fill) fill.style.width = pct + '%';
    if (pctEl) pctEl.textContent = pct;
    if (bytesEl && state.file) {
      var total = state.file.size;
      var uploaded = state.progress * total;
      bytesEl.textContent = formatBytesShort(uploaded) + ' / ' + formatBytesShort(total);
    }
  }
  function formatBytesShort(b) {
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
    if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
  }
  function renderSuccessView() {
    var btn = $('[data-ugc-copycode]');
    var codeEl = $('[data-ugc-promo-code]');
    if (btn) btn.dataset.code = state.promoCode;
    if (codeEl) codeEl.textContent = state.promoCode;
    var reup = $('[data-ugc-reupload]');
    if (reup) reup.style.display = (state.uploadCount >= MAX_UPLOADS) ? 'none' : '';
    card.classList.add('as-upsellCard--uploaded');
    gtm('ugc_promo_displayed');
  }
  function renderLimitView() {
    var mailtos = $$('[data-ugc-mailto]');
    var subj = encodeURIComponent('Video upload — order ' + (CFG.orderNumber || ''));
    for (var i = 0; i < mailtos.length; i++) {
      mailtos[i].setAttribute('href', 'mailto:' + SUPPORT_EMAIL + '?subject=' + subj);
    }
    gtm('ugc_limit_reached');
  }
  function buildSupportMailtoHref() {
    var subj = encodeURIComponent('My AstroSong video upload — Order #' + (CFG.orderNumber || ''));
    var body = encodeURIComponent(
      "Hi AstroSong team,\n\n" +
      "My video upload didn't make it through. Could you add it for me?\n\n" +
      "(Please attach your video below — any size, any format works.)\n\n" +
      'Order: #' + (CFG.orderNumber || '') + "\n" +
      'Reveal page: ' + (CFG.pageHandle || '') + "\n\n" +
      'Thanks!'
    );
    return 'mailto:' + SUPPORT_EMAIL + '?subject=' + subj + '&body=' + body;
  }

  /* No raw technical error string is shown to the customer — tech detail stays
     in console + GA for our debugging. Two flavors of error UX based on whether
     the Drive upload made it: */
  function renderErrorView() {
    var headline = $('[data-ugc-error-headline]');
    var subtitle = $('[data-ugc-error-subtitle]');
    var mailtoBtn = $('[data-ugc-mailto-cta]');
    var retryBtn = $('[data-ugc-retry]');
    var fallback = $('[data-ugc-error-fallback]');
    var fallbackLink = $('[data-ugc-mailto-fallback]');
    var mailtoHref = buildSupportMailtoHref();

    if (state.pendingFileId) {
      /* Drive upload succeeded but finalize failed. Worker is idempotent + atomic
         now, so a finalize-only retry has a high chance of succeeding — keep it
         as primary CTA. Mailto stays as secondary fallback. */
      if (headline) headline.textContent = 'Almost there!';
      if (subtitle) subtitle.textContent = 'Your video is uploaded. One last step to claim your reward code.';
      if (mailtoBtn) mailtoBtn.hidden = true;
      if (retryBtn) { retryBtn.hidden = false; retryBtn.textContent = 'Confirm my video'; }
      if (fallback) fallback.hidden = false;
      if (fallbackLink) fallbackLink.setAttribute('href', mailtoHref);
    } else {
      /* Upload itself failed (init or chunk PUT exhausted retries). Re-running
         startUpload would hit the same failure mode. Skip the doomed-retry CTA
         and route the customer straight to email — we'll pick it up manually. */
      if (headline) headline.textContent = "Hmm — your video didn't make it through.";
      if (subtitle) subtitle.textContent = "It happens. Email your video directly to us — we'll add it manually and send your reward code within 24h.";
      if (mailtoBtn) { mailtoBtn.hidden = false; mailtoBtn.setAttribute('href', mailtoHref); }
      if (retryBtn) retryBtn.hidden = true;
      if (fallback) fallback.hidden = true;
    }
  }

  function updateNoteCounter() {
    var ta = $('#ugcNoteInput');
    var counter = $('[data-ugc-notelen]');
    if (ta && counter) counter.textContent = ta.value.length;
  }

  /* ---------- Flow handlers ---------- */
  function onFileSelected(file) {
    var err = validateFile(file);
    var validationEl = $('[data-ugc-validation]');
    if (err) {
      if (validationEl) { validationEl.textContent = err; validationEl.hidden = false; }
      gtm('ugc_upload_failed', { error_type: 'validation', file_size_mb: file ? Math.round(file.size / 1048576) : 0 });
      return;
    }
    if (validationEl) validationEl.hidden = true;
    state.file = file;
    state.source = 'upload'; /* file picker = filmed on another device */
    saveStored({ fileMeta: { name: file.name, size: file.size, type: file.type } });
    gtm('ugc_file_selected', {
      file_size_mb: Math.round(file.size / 1048576),
      file_type: file.type || getExt(file.name),
      source: 'upload'
    });
    setView('selected');
    renderSelectedView();
  }

  function startUpload() {
    if (!state.file) { setView('idle'); return; }
    var ta = $('#ugcNoteInput');
    state.note = (ta && ta.value) || '';
    saveStored({ note: state.note });
    if (state.note) gtm('ugc_note_added', { note_length: state.note.length });

    state.progress = 0;
    setView('uploading');
    renderUploadingView();
    gtm('ugc_upload_started', { file_size_mb: Math.round(state.file.size / 1048576), source: state.source || 'upload' });

    var midProgressFired = false;
    var failureStep = 'init';

    callInitUpload(state.file).then(function (res) {
      state.sessionUri = res.session_uri;
      state.uploadIndex = res.upload_index;
      failureStep = 'upload';
      return uploadFileToSession(state.file, res.session_uri, function (p) {
        state.progress = p;
        renderUploadingView();
        if (!midProgressFired && p >= 0.5) {
          midProgressFired = true;
          gtm('ugc_upload_progress_50');
        }
      });
    }).then(function (driveMeta) {
      /* CRITICAL: persist the Drive file_id BEFORE finalize. If finalize then fails,
         the retry path skips re-upload and calls finalize-only with this same id —
         preventing the Drive duplicates we used to create on every finalize crash. */
      state.pendingFileId = driveMeta.id;
      state.pendingUploadIndex = state.uploadIndex;
      saveStored({ pendingFileId: driveMeta.id, pendingUploadIndex: state.uploadIndex });
      failureStep = 'finalize';
      return callFinalizeUpload(driveMeta.id, state.note, state.uploadIndex);
    }).then(function (final) {
      state.promoCode = final.promo_code;
      state.uploadCount = final.upload_count;
      state.pendingFileId = null;
      saveStored({ promoCode: final.promo_code, uploadCount: final.upload_count });
      clearStoredFile();
      clearStoredPending();
      /* Clear the on-device recording too (incl. recorder hand-offs) so we never
         re-offer "See your reaction video" after a successful send. */
      if (typeof window.astrosongUGCClearPending === 'function') window.astrosongUGCClearPending();
      gtm('ugc_upload_succeeded', {
        upload_index: state.uploadIndex,
        file_size_mb: Math.round(state.file.size / 1048576),
        source: state.source || 'upload'
      });
      setView('success');
      renderSuccessView();
    }).catch(function (err) {
      if (err && err.message === 'cancelled') return; /* user cancelled — handler already swapped view */
      var detail = err && err.detail;
      var code = (detail && detail.error) || (err && err.message) || 'unknown';
      console.error('[UGC] upload error at step=' + failureStep, err, detail);
      if (err && err.status === 403 && code === 'upload_limit_reached') {
        renderLimitView();
        setView('limit');
        gtm('ugc_upload_failed', { error_type: 'limit_reached', failure_step: failureStep });
        return;
      }
      gtm('ugc_upload_failed', { error_type: code, failure_step: failureStep });
      renderErrorView();
      setView('error');
    });
  }

  /* Re-runs finalize for an already-uploaded Drive file. Used when:
   *   (a) a previous attempt's finalize failed but the Drive upload succeeded
   *   (b) the user re-opens the page after a failure (auto-recovery on init)
   * The worker is idempotent: if this file_id is already finalized it returns
   * the existing state without minting a duplicate discount code. */
  function finalizePending() {
    if (!state.pendingFileId) { setView('idle'); return; }
    state.progress = 1;
    setView('uploading');
    renderUploadingView();
    gtm('ugc_upload_started', { failure_step: 'finalize-only-retry', file_size_mb: 0 });

    /* Keep the screen awake across the finalize-only POST too. No resume-on-wake
       needed: finalize is idempotent and auto-re-runs on the next page load. */
    acquireWakeLock();
    callFinalizeUpload(state.pendingFileId, state.note || '', state.pendingUploadIndex || 1)
      .then(function (final) {
        state.promoCode = final.promo_code;
        state.uploadCount = final.upload_count;
        state.pendingFileId = null;
        saveStored({ promoCode: final.promo_code, uploadCount: final.upload_count });
        clearStoredFile();
        clearStoredPending();
        gtm('ugc_upload_succeeded', { upload_index: final.upload_count || 1, file_size_mb: 0, recovered: true });
        setView('success');
        renderSuccessView();
      })
      .catch(function (err) {
        var detail = err && err.detail;
        var code = (detail && detail.error) || (err && err.message) || 'unknown';
        console.error('[UGC] finalize-only retry failed', err, detail);
        gtm('ugc_upload_failed', { error_type: code, failure_step: 'finalize-only-retry' });
        renderErrorView();
        setView('error');
      })
      .then(releaseWakeLock, releaseWakeLock); /* release on success AND error (the .catch resolves) */
  }

  /* ---------- Resume an on-device recording (from IndexedDB) ---------- */
  function resumeSend() {
    var rec = state.pendingRecording;
    if (!rec || !rec.blob) { setView('idle'); return; }
    var type = rec.type || 'video/mp4';
    var ext = type.indexOf('webm') > -1 ? 'webm' : 'mp4';
    var file = new File([rec.blob], 'reaction-' + (rec.ts || Date.now()) + '.' + ext, { type: type });
    state.progress = 0;
    setView('uploading');
    renderUploadingView();
    gtm('ugc_recording_resume_send');
    uploadFileCore(file, rec.note || '', function (p) { state.progress = p; renderUploadingView(); })
      .then(function () {
        if (typeof window.astrosongUGCClearPending === 'function') window.astrosongUGCClearPending();
        state.pendingRecording = null;
        /* uploadFileCore already flipped the card to success + rendered the code */
      })
      .catch(function (err) {
        console.error('[UGC] resume send failed', err);
        gtm('ugc_upload_failed', { error_type: (err && err.message) || 'unknown', failure_step: 'resume' });
        renderErrorView();
        setView('error');
      });
  }
  function resumeDiscard() {
    if (typeof window.astrosongUGCClearPending === 'function') window.astrosongUGCClearPending();
    state.pendingRecording = null;
    gtm('ugc_recording_resume_discarded');
    setView('idle');
  }

  /* ---------- Wire UI ---------- */
  function wire() {
    /* Card "Film my reaction (this phone)" → launch the in-browser recorder.
       The card is the persistent home: re-record after an interruption, or
       record at all if she skipped the first-play popup. */
    var recordBtn = card.querySelector('[data-ugc-record]');
    if (recordBtn) recordBtn.addEventListener('click', function () {
      gtm('ugc_card_record_clicked');
      if (typeof window.astrosongUGCOpenRecorder === 'function') window.astrosongUGCOpenRecorder();
    });

    /* Resume an on-device recording → REVIEW it in the recorder (watch, then
       send / keep private / re-record) instead of sending blind. */
    var resumeReviewBtn = $('[data-ugc-resume-review]');
    if (resumeReviewBtn) resumeReviewBtn.addEventListener('click', function () {
      var rec = state.pendingRecording;
      if (rec && rec.blob && typeof window.astrosongUGCReviewBlob === 'function') {
        gtm('ugc_recording_review_opened');
        window.astrosongUGCReviewBlob(rec.blob);
      } else { setView('idle'); }
    });
    var resumeDiscardBtn = $('[data-ugc-resume-discard]');
    if (resumeDiscardBtn) resumeDiscardBtn.addEventListener('click', resumeDiscard);

    /* File picker */
    var fileInput = $('#ugcFileInput');
    if (fileInput) fileInput.addEventListener('change', function () {
      if (fileInput.files && fileInput.files[0]) onFileSelected(fileInput.files[0]);
    });

    /* Change file */
    var changeBtn = $('[data-ugc-change]');
    if (changeBtn) changeBtn.addEventListener('click', function () {
      state.file = null;
      clearStoredFile();
      if (fileInput) fileInput.value = '';
      setView('idle');
    });

    /* Note typing */
    var noteInput = $('#ugcNoteInput');
    if (noteInput) noteInput.addEventListener('input', function () {
      updateNoteCounter();
      saveStored({ note: noteInput.value });
    });

    /* Send */
    var sendBtn = $('[data-ugc-send]');
    if (sendBtn) sendBtn.addEventListener('click', startUpload);

    /* Terms link tracking */
    var termsLink = card.querySelector('[data-ugc-terms-link]');
    if (termsLink) termsLink.addEventListener('click', function () { gtm('ugc_terms_clicked'); });

    /* Promo code copy */
    var copyBtn = $('[data-ugc-copycode]');
    var hint = $('[data-ugc-promo-hint]');
    if (copyBtn) copyBtn.addEventListener('click', function () {
      var code = copyBtn.dataset.code || '';
      function showCopied() {
        copyBtn.classList.add('copied');
        if (hint) hint.textContent = 'Copied! ✓';
        setTimeout(function () {
          copyBtn.classList.remove('copied');
          if (hint) hint.textContent = 'Tap to copy';
        }, 1500);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(showCopied).catch(function () {
          fallbackCopy(code); showCopied();
        });
      } else { fallbackCopy(code); showCopied(); }
      gtm('ugc_promo_copied');
    });

    /* Claim free AstroSong (drives a new order → potentially a new reaction) */
    var claimBtn = $('[data-ugc-claim]');
    if (claimBtn) claimBtn.addEventListener('click', function () { gtm('ugc_claim_clicked'); });

    /* Re-upload */
    var reupBtn = $('[data-ugc-reupload]');
    if (reupBtn) reupBtn.addEventListener('click', function () {
      if (state.uploadCount >= MAX_UPLOADS) { renderLimitView(); setView('limit'); return; }
      state.file = null;
      if (fileInput) fileInput.value = '';
      gtm('ugc_reupload_started', { upload_index: state.uploadCount + 1 });
      setView('idle'); /* setView reveals [data-ugc-back-to-code] because a promo exists */
    });

    /* Back to my reward code — escape hatch from the re-upload (idle) state back
       to the success view. Fixes the "my code disappeared" panic: the code was
       never lost, idle just didn't render it and offered no way back but reload. */
    var backToCodeBtn = $('[data-ugc-back-to-code]');
    if (backToCodeBtn) backToCodeBtn.addEventListener('click', function () {
      state.file = null;
      if (fileInput) fileInput.value = '';
      gtm('ugc_reupload_cancelled');
      setView('success');
      renderSuccessView();
    });

    /* Retry button is now only shown when state.pendingFileId is set (the
       error view hides it otherwise). The only valid retry path is finalize-
       only, since re-running the doomed upload step would just fail again. */
    var retryBtn = $('[data-ugc-retry]');
    if (retryBtn) retryBtn.addEventListener('click', function () {
      if (state.pendingFileId) finalizePending();
    });

    /* Cancel upload */
    var cancelBtn = $('[data-ugc-cancel]');
    if (cancelBtn) cancelBtn.addEventListener('click', function () {
      if (state.abort) try { state.abort(); } catch (e) {}
      state.abort = null;
      gtm('ugc_upload_cancelled');
      setView('selected');
      renderSelectedView();
    });

    /* Resume-on-wake: the Low-Power-Mode backstop. If iOS rejected the wake lock
       (or it lapsed), the screen can still lock → Safari suspends the page → the
       in-flight chunk XHR stalls. When the page is resumed (visible again), if a
       chunk transfer is mid-flight (state.resumeKick is live — set only inside
       uploadFileToSession, nulled on settle) re-acquire the lock and resume from
       the last confirmed byte. No-ops at every other time (idle, success,
       finalize-only, after completion). */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && typeof state.resumeKick === 'function') {
        gtm('ugc_upload_resumed_on_wake');
        /* Re-arm the screen lock on EVERY wake during an upload, BEFORE resumeKick.
           resumeKick early-returns while a genuine-network retry backoff is pending
           (recovering===true), so re-acquiring inside it would miss that window and
           the screen could sleep again for the rest of that cycle. acquireWakeLock
           is idempotent (wakeLock||wakePending guard) so the extra call is free and
           cannot affect the no-double-upload guarantees. */
        try { acquireWakeLock(); } catch (e) {}
        try { state.resumeKick(); } catch (e) {}
      }
    });
  }

  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {}
  }

  /* ---------- Init ---------- */
  function init() {
    /* Hydrate from localStorage if metafield not yet reflected */
    var saved = loadStored();
    if (!state.promoCode && saved.promoCode) state.promoCode = saved.promoCode;
    if (!state.uploadCount && saved.uploadCount) state.uploadCount = saved.uploadCount;
    if (saved.note) state.note = saved.note;
    if (saved.pendingFileId) {
      state.pendingFileId = saved.pendingFileId;
      state.pendingUploadIndex = saved.pendingUploadIndex || 0;
    }

    wire();

    /* Decide initial view */
    if (state.promoCode) {
      if (state.uploadCount >= MAX_UPLOADS) {
        renderLimitView();
        setView('limit');
      } else {
        setView('success');
        renderSuccessView();
      }
    } else if (state.pendingFileId) {
      /* Auto-recovery: previous upload made it to Drive but never finalized.
         Idempotent worker means this is safe — if it was actually finalized
         server-side and we just missed the response, we get the existing code. */
      finalizePending();
    } else {
      setView('idle');
      /* Resume an unsent recording saved on this device (failed upload, reload,
         app-switch). Async — flips to the resume view if a blob is waiting. */
      idbGetPending().then(function (rec) {
        if (rec && rec.blob && rec.blob.size > 0 && !state.promoCode && state.view === 'idle') {
          state.pendingRecording = rec;
          setView('resume');
          gtm('ugc_recording_resume_offered');
        }
      }).catch(function () {});
    }

    /* Card impression tracking (only on idle/initial visible state) */
    if ('IntersectionObserver' in window) {
      var seen = false;
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting && !seen) {
            seen = true;
            gtm('ugc_card_viewed');
            io.disconnect();
          }
        }
      }, { threshold: 0.5 });
      io.observe(card);
    }

    /* Async verify against worker — worker is the source of truth, reconcile any stale localStorage */
    callStatus().then(function (s) {
      if (s.promo_code) {
        /* Worker has a code — hydrate if we didn't have one, or if it differs */
        if (state.promoCode !== s.promo_code || state.uploadCount !== (s.upload_count || 0)) {
          state.promoCode = s.promo_code;
          state.uploadCount = s.upload_count || 1;
          saveStored({ promoCode: s.promo_code, uploadCount: s.upload_count || 1 });
          if (state.view === 'idle' || state.view === 'error') {
            setView('success');
            renderSuccessView();
          }
        }
      } else {
        /* Worker says no code — clear stale localStorage and reset to idle if we were showing success */
        if (state.promoCode) {
          try {
            var cur = loadStored();
            delete cur.promoCode;
            delete cur.uploadCount;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cur));
          } catch (e) {}
          state.promoCode = '';
          state.uploadCount = 0;
          card.classList.remove('as-upsellCard--uploaded');
          if (state.view === 'success' || state.view === 'limit') setView('idle');
        }
      }
    }).catch(function () {});
  }

  /* ---------- Reusable upload API (used by the recorder's send screen) ----------
   * Runs the full pipeline (init → chunked resumable upload → finalize) and
   * persists the promo so it survives reloads AND shows in the card afterwards.
   * Returns a Promise<{promo_code, upload_count}>; reports progress via onProgress.
   * Throws on failure (caller renders its own error UI). */
  function uploadFileCore(file, note, onProgress) {
    var err = validateFile(file);
    if (err) {
      gtm('ugc_upload_failed', { error_type: 'validation', failure_step: 'api' });
      return Promise.reject(new Error(err));
    }
    state.file = file;
    gtm('ugc_upload_started', { file_size_mb: Math.round(file.size / 1048576), source: 'recorder' });

    function persistSuccess(final) {
      state.promoCode = final.promo_code;
      state.uploadCount = final.upload_count;
      state.pendingFileId = null;
      state.note = note || '';
      saveStored({ promoCode: final.promo_code, uploadCount: final.upload_count });
      clearStoredFile();
      clearStoredPending();
      /* Clear the on-device recording too (incl. recorder hand-offs) so we never
         re-offer "See your reaction video" after a successful send. */
      if (typeof window.astrosongUGCClearPending === 'function') window.astrosongUGCClearPending();
      gtm('ugc_upload_succeeded', { upload_index: final.upload_count || 1, file_size_mb: Math.round(file.size / 1048576), source: 'recorder' });
      /* Reflect in the card so the code stays on the page after she's done. */
      try { card.classList.add('as-upsellCard--uploaded'); setView('success'); renderSuccessView(); } catch (e) {}
      return final;
    }

    /* Retry that skips re-upload: a prior attempt already reached Drive but
       finalize failed → finalize-only (idempotent worker, no Drive duplicate). */
    if (state.pendingFileId) {
      if (onProgress) onProgress(1);
      return callFinalizeUpload(state.pendingFileId, note || '', state.pendingUploadIndex || 1).then(persistSuccess);
    }

    var uploadIndex = 0;
    return callInitUpload(file).then(function (res) {
      uploadIndex = res.upload_index;
      return uploadFileToSession(file, res.session_uri, onProgress);
    }).then(function (driveMeta) {
      /* persist Drive id BEFORE finalize so a finalize-only retry can't dup */
      state.pendingFileId = driveMeta.id;
      state.pendingUploadIndex = uploadIndex;
      saveStored({ pendingFileId: driveMeta.id, pendingUploadIndex: uploadIndex });
      return callFinalizeUpload(driveMeta.id, note || '', uploadIndex);
    }).then(persistSuccess);
  }
  window.astrosongUGCUploadFile = uploadFileCore;

  /* ---------- Public bridge for the in-browser recorder (legacy fallback) ----------
   * The recorder (assets/as-ugc-recorder.js) produces a Blob and wraps it
   * in a File, then calls this bridge to hand off into the existing upload
   * pipeline. Bypasses the file picker — we already have the file. */
  window.astrosongUGCStartUploadFromFile = function (file, opts) {
    if (!file) return;
    /* Sample-accurate song-start offset from the recorder (REVEAL tap). Recorder
       uploads only; null/undefined for the legacy file picker -> worker omits it. */
    state.songStartMs = (opts && typeof opts.songStartMs === 'number') ? opts.songStartMs : null;
    var err = validateFile(file);
    if (err) {
      console.error('[UGC bridge] validateFile rejected:', err);
      gtm('ugc_upload_failed', { error_type: 'validation', failure_step: 'bridge' });
      return;
    }
    state.file = file;
    state.source = 'recorder'; /* in-browser recorder hand-off */
    saveStored({ fileMeta: { name: file.name, size: file.size, type: file.type } });
    gtm('ugc_file_selected', {
      file_size_mb: Math.round(file.size / 1048576),
      file_type: file.type || getExt(file.name),
      source: 'recorder',
    });
    /* Show just the card (scroll it into view) — the recorder overlay is closing. */
    if (card) { try { card.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {} }
    setView('selected');
    renderSelectedView();
    /* Auto-kick the upload so the customer doesn't have to confirm again —
       she already approved by tapping "Send my reaction". */
    startUpload();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
